import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GroupIcon from '@mui/icons-material/Group';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import PlaceIcon from '@mui/icons-material/Place';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { FC, MutableRefObject } from 'react';
import {
  convertTimestampToTime,
  formatDiscipline,
  formatHippodromeName,
  getStatus,
} from '../../../../utils/format';
import { FlattedCourse } from '../../services/type';
import { StatusCourse } from '../../services/enum';

interface Props {
  course: FlattedCourse;
  reference?: MutableRefObject<HTMLDivElement | null>;
  onClick: (course: FlattedCourse) => void;
}

const CourseCard: FC<Props> = ({
  course,
  reference,
  onClick,
}) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const getQuintePlusIcon = () => {
    return <Box bgcolor={"red"} position={'absolute'} bottom={0} right={0} borderRadius={2} px={0.2}>
      <Typography color='white' variant='subtitle1' fontSize={isMobile ? 12 : 16}>Q+</Typography>
    </Box>
  }

  // Header de la carte
  const header = (
    <Box
      width={isMobile ? '18vw' : '15%'}
      position={'relative'}
      height="100%"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      borderRadius="12px 0 0 12px"
      color={'secondary.main'}
      bgcolor={"primary.main"}
    >
      <Typography fontWeight={700} fontSize={22} lineHeight={1}>
        R{course.reunionNum}
      </Typography>
      <Typography fontWeight={700} fontSize={22} lineHeight={1}>
        C{course.courseNum}
      </Typography>
      {course.course.paris.map(p => p.typePari).includes("QUINTE_PLUS") && getQuintePlusIcon()}
    </Box>
  );

  const content = (
    <Box
      py={1}
      px={isMobile ? 0 : 1}
      display="flex"
      flexDirection="column"
      gap={0.5}
      width={isMobile ? "50%" : "60%"}
    >
      {/* NOM COURSE */}
      <Box display="flex" flexDirection={'row'} justifyContent={'flex-start'} alignItems={'center'} gap={1}>
        <EmojiEventsIcon sx={{ fontSize: 18, color: 'primary.light' }} />
        <Typography
          fontSize={isMobile ? 15 : 17}
          fontWeight={600}
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {course.course.libelle}
        </Typography>
      </Box>

      {/* HIPPPODROME */}
      <Box display="flex" alignItems="center" gap={1} >
        <PlaceIcon sx={{ fontSize: 16, color: 'primary.light' }} />
        <Typography
          fontSize={13}
          color="text.secondary"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {formatHippodromeName(course.hippodrome)}
        </Typography>
      </Box>

      {/* DISCIPLINE */}
      <Box display="flex" alignItems="center" gap={1}>
        <OpenInFullIcon sx={{ fontSize: 16, color: 'primary.light' }} />
        <Typography
          fontSize={13}
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {formatDiscipline(course.course.discipline, isMobile)} • {course.course.distance}m
        </Typography>
      </Box>
    </Box>
  );



  const footer = (
    <Box
      py={1}
      display="flex"
      flexDirection="column"
      alignItems="flex-end"
      justifyContent="space-between"
      width={isMobile ? "25%" : "18%"}
    >
      <Typography fontSize={14} fontWeight={600} color='primary.main'>
        🏇 {course.course.nombreDeclaresPartants}
      </Typography>

      <Typography fontSize={15} fontWeight={600} color='primary.main'>
        ⏱ {convertTimestampToTime(course.time)}
      </Typography>

      <Typography
        fontSize={14}
        fontWeight={700}
        color={
          course.course.categorieStatut === StatusCourse.ANNULEE
            ? 'error.main'
            : 'success.main'
        }
      >
        {getStatus(course.course.categorieStatut)}
      </Typography>
    </Box>

  );
  // Structure de la carte

  return (
    <Box
      ref={reference}
      onClick={() => onClick(course)}
      width={isMobile ? "100%" : "95%"}
      height="90px"
      borderRadius={2}
      display="flex"
      flexDirection="row"
      gap={1}
      sx={(theme) => {
        return {
          cursor: 'pointer',

          /* Statut ARRIVÉE */
          opacity:
            course.course.categorieStatut === StatusCourse.ARRIVEE ||
              course.course.categorieStatut === StatusCourse.ANNULEE
              ? 0.6
              : 1,

          /* 🎨 Fond plus premium */
          background: `linear-gradient(
            180deg,
            #ffffff 0%,
            ${theme.palette.grey[50]} 100%
          )`,

          /* 🌫️ Ombre */
          boxShadow: '0px 4px 21px rgba(0,0,0,0.25)',

          /* 🌀 Animation douce */
          transition: 'all 0.25s ease',

          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0px 10px 28px rgba(0,0,0,0.3)',
          },
        };
      }}
    >
      {header}
      {content}
      {footer}
    </Box>
  );
};

export default CourseCard;
